import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '@env/environment';
import { ACCESS_TOKEN_COOKIE } from '@core/auth.constants';

interface UserProfile {
  id?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

/**
 * Selects which token a method should read.
 * - `Active`   — impersonation token if "signed-in-as-user-token" is set,
 *                else the cookie-stored original. Default for API calls.
 * - `Original` — always the cookie-stored original token, even during
 *                impersonation. Use for UI / route gating.
 */
export enum TokenSource {
  Active = 'active',
  Original = 'original',
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private keycloak = new Keycloak({
    url: environment.keycloak.url,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId
  });

  async init(): Promise<boolean> {
    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
        checkLoginIframe: false
      });

      if (authenticated && this.keycloak.token) {
        this.setCookie(ACCESS_TOKEN_COOKIE, this.keycloak.token);
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error('Keycloak init failed:', error);
      return false;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getCookie(ACCESS_TOKEN_COOKIE);
    if (!token) return false;

    // Тестовый токен
    if (environment?.test?.accessToken && token === environment.test.accessToken) {
      return true;
    }

    // Проверяем что JWT не истёк и выпущен нашим issuer'ом
    // (защита от утёкшей куки с чужого окружения — prod cookie на test домене и наоборот)
    try {
      const decoded = this.decodeJWT(token);
      if (decoded.exp * 1000 <= Date.now()) {
        this.deleteCookie(ACCESS_TOKEN_COOKIE);
        return false;
      }
      const expectedIssuer = `${environment.keycloak.url.replace(/\/$/, '')}/realms/${environment.keycloak.realm}`;
      if (decoded.iss && decoded.iss !== expectedIssuer) {
        this.deleteCookie(ACCESS_TOKEN_COOKIE);
        return false;
      }
      return true;
    } catch {
      this.deleteCookie(ACCESS_TOKEN_COOKIE);
      return false;
    }
  }

  redirectToAuth(): void {
    this.keycloak.login();
  }

  logout(): void {
    this.deleteCookie(ACCESS_TOKEN_COOKIE);
    localStorage.removeItem('user');
    localStorage.removeItem('selected_space');
    this.keycloak.logout({
      redirectUri: window.location.origin + '/login'
    });
  }

  /**
   * Which token to read.
   * - `Active` — impersonation token if set, else original. Default for API calls.
   * - `Original` — always the cookie-stored original token, even while
   *   impersonating. Use for UI / route gating so an admin keeps admin
   *   access while acting as another user.
   */
  getAccessToken(source: TokenSource = TokenSource.Active): string | null {
    if (source === TokenSource.Original) return this.getCookie(ACCESS_TOKEN_COOKIE);
    const signedInAsToken = localStorage.getItem('signed-in-as-user-token');
    if (signedInAsToken) return signedInAsToken;
    return this.getCookie(ACCESS_TOKEN_COOKIE);
  }

  getUserProfile(): UserProfile | null {
    const token = this.getCookie(ACCESS_TOKEN_COOKIE);
    if (token == null) return null;

    // Тестовый пользователь
    if (environment?.test?.accessToken && token === environment.test.accessToken && !environment.production) {
      return {
        id: '12345678-1234-5678-9abc-123456789012',
        email: 'test@test.com',
        name: 'test test',
        given_name: 'test',
        family_name: 'test'
      };
    }

    const decoded = this.decodeJWT(token);
    return {
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      given_name: decoded.given_name,
      family_name: decoded.family_name
    } as UserProfile;
  }

  saveUserProfileToLocalStorage(): void {
    const userProfile = this.getUserProfile();
    if (userProfile != null) {
      localStorage.setItem('user', JSON.stringify(userProfile));
    }
  }

  getUserProfileFromLocalStorage(): UserProfile | null {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '') as UserProfile;
    } catch {
      return null;
    }
  }

  private setupTokenRefresh(): void {
    setInterval(async () => {
      try {
        const refreshed = await this.keycloak.updateToken(60);
        if (refreshed && this.keycloak.token) {
          this.setCookie(ACCESS_TOKEN_COOKIE, this.keycloak.token);
        }
      } catch {
        console.error('Token refresh failed');
      }
    }, 30000);
  }

  private setCookie(name: string, value: string, days: number = 7): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

    const { domainPart, securePart } = this.getCookieAttrs();

    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}${domainPart}; path=/; samesite=lax${securePart}`;
  }

  // Кука пишется на registrable domain (последние 2 label'а hostname) —
  // чтобы лендинг на корневом домене видел логин с субдомена.
  // Cross-env защита — через проверку iss в isAuthenticated().
  private getCookieAttrs(): { domainPart: string; securePart: string } {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    if (isLocalhost) return { domainPart: '', securePart: '' };

    const parts = hostname.split('.');
    const domainPart = parts.length >= 2 ? `; domain=.${parts.slice(-2).join('.')}` : '';
    return { domainPart, securePart: '; secure' };
  }

  private deleteCookie(name: string): void {
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';

    // Host-only
    document.cookie = `${name}=; ${expired}; path=/;`;

    // Все суффиксы текущего hostname — чтобы потереть куки с любым domain-scope'ом,
    // включая legacy записи от прошлых версий кода.
    const parts = window.location.hostname.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      const domain = parts.slice(i).join('.');
      document.cookie = `${name}=; ${expired}; path=/; domain=.${domain};`;
      document.cookie = `${name}=; ${expired}; path=/; domain=${domain};`;
    }
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  private decodeJWT(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  }

  /**
   * Роли юзера в `resource_access[<keycloak clientId>].roles`.
   * `source` selects which token to read — see {@link TokenSource}.
   */
  getClientRoles(source: TokenSource = TokenSource.Active): string[] {
    const token = this.getAccessToken(source);
    if (!token) return [];
    try {
      const decoded = this.decodeJWT(token);
      const roles = decoded?.resource_access?.[environment.keycloak.clientId]?.roles;
      return Array.isArray(roles) ? roles : [];
    } catch {
      return [];
    }
  }

  hasClientRole(role: string, source: TokenSource = TokenSource.Active): boolean {
    // Тест-токен — wildcard на все роли (для локальной разработки)
    const token = this.getAccessToken(source);
    if (token && environment?.test?.accessToken && token === environment.test.accessToken) return true;
    return this.getClientRoles(source).includes(role);
  }

  hasAnyClientRole(roles: string[], source: TokenSource = TokenSource.Active): boolean {
    const token = this.getAccessToken(source);
    if (token && environment?.test?.accessToken && token === environment.test.accessToken) return true;
    const userRoles = this.getClientRoles(source);
    return roles.some(r => userRoles.includes(r));
  }
}
